import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

// Shared Screen Wake Lock (works on the website AND in the Android WebView)
let sentinel = null;
let holders = 0;

async function ensureLock() {
  if (holders <= 0 || sentinel) return;
  if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
  try {
    if ('wakeLock' in navigator) {
      sentinel = await navigator.wakeLock.request('screen');
      sentinel.addEventListener('release', () => { sentinel = null; });
    }
  } catch (_) { sentinel = null; }
}

function releaseLock() {
  if (sentinel) { try { sentinel.release(); } catch (_) {} sentinel = null; }
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') ensureLock();
  });
}

/**
 * Per-module "keep screen on" preference.
 * Default: OFF → the screen can sleep, the session keeps playing in background.
 * ON → holds a screen wake lock while the module is open.
 */
export default function useKeepAwake(moduleKey, active = true) {
  const storageKey = `hypnose_keep_awake_${moduleKey}`;
  const [keepAwake, setKeepAwake] = useState(() => {
    try { return localStorage.getItem(storageKey) === 'true'; } catch (_) { return false; }
  });

  useEffect(() => {
    if (!active || !keepAwake) return;
    holders++;
    ensureLock();
    return () => {
      holders = Math.max(0, holders - 1);
      if (holders === 0) releaseLock();
    };
  }, [active, keepAwake]);

  const toggle = useCallback(() => {
    setKeepAwake((prev) => {
      const next = !prev;
      try { localStorage.setItem(storageKey, String(next)); } catch (_) {}
      toast(next
        ? "🔆 L'écran restera allumé pendant la séance"
        : "🌙 L'écran pourra se mettre en veille — la séance continue", { duration: 2500 });
      return next;
    });
  }, [storageKey]);

  return [keepAwake, toggle];
}
